import type { EnterpriseUserAdminView_ColumnName } from '__generated__/graphql-types';

import type { Column, Learner, LearnerStatus } from 'bundles/enterprise-admin-learners/types';

import _t from 'i18n!nls/enterprise-admin-learners';

export const DEFAULT_LEARNER_STATUSES: LearnerStatus[] = ['JOINED'];

// All selectable membership statuses, in display order. Used by the new
// LearnerListView, whose status filter defaults to "All".
export const ALL_LEARNER_STATUSES: LearnerStatus[] = ['INVITED', 'JOINED', 'REMOVED'];

export const LEARNER_SORT_KEY: { [key in keyof Partial<Learner>]: EnterpriseUserAdminView_ColumnName } = {
  removalDate: 'DELETED_AT',
  invitationDate: 'INVITED_AT',
  joinDate: 'JOINED_AT',
  latestActivity: 'LATEST_ACTIVITY_AT',
  name: 'NAME',
  enrollments: 'NUM_ENROLLMENTS',
  completions: 'NUM_COMPLETIONS',
} as const;

/**
 * Column options for the learner tables.
 *
 * `enableNewLearnersView` opts into the new LearnerListView surface (All Learners /
 * Group Details): the program column reads as "Groups" and the default visible set
 * matches the finalized designs. When omitted (legacy LearnersPage /
 * ProgramLearnersPage / their column drawer), behavior is unchanged.
 */
export const getLearnerTableColumnOptions = ({
  programLevel,
  enableNewLearnersView = false,
}: {
  programLevel: boolean;
  enableNewLearnersView?: boolean;
}): Column[] => {
  const programsColumn: Column = {
    accessor: 'programs',
    label: enableNewLearnersView ? _t('Groups') : _t('Programs'),
    default: true,
  };
  return [
    {
      accessor: 'name',
      label: _t('Learner name'),
      default: true,
    },
    {
      accessor: 'status',
      label: _t('Status'),
      default: true,
    },
    ...(programLevel ? [] : [programsColumn]),
    {
      accessor: 'invitationDate',
      label: _t('Invitation date'),
      default: enableNewLearnersView,
    },
    {
      accessor: 'joinDate',
      label: _t('Join Date'),
      default: !enableNewLearnersView,
    },
    {
      accessor: 'latestActivity',
      label: _t('Last Activity'),
      default: !enableNewLearnersView || programLevel,
    },
    {
      accessor: 'removalDate',
      label: _t('Removal Date'),
    },
    {
      accessor: 'allowReturn',
      label: _t('Allow return'),
    },
    {
      accessor: 'products',
      label: _t('Current Enrollments'),
    },
    {
      accessor: 'contracts',
      label: _t('Contracts'),
    },
    {
      accessor: 'enrollments',
      label: _t('Enrollments'),
      default: enableNewLearnersView,
    },
    {
      accessor: 'completions',
      label: _t('Completions'),
    },
    {
      accessor: 'location',
      label: _t('Location'),
    },
    {
      accessor: 'jobTitle',
      label: _t('Job title'),
    },
    {
      accessor: 'jobType',
      label: _t('Job type'),
    },
    {
      accessor: 'managerName',
      label: _t('Manager'),
    },
    {
      accessor: 'businessUnits',
      label: _t('Business units'),
    },
    {
      accessor: 'externalId',
      label: _t('External ID'),
    },
  ] as const;
};

export const getDefaultLearnerTableColumns = ({
  programLevel,
  enableNewLearnersView = false,
}: {
  programLevel: boolean;
  enableNewLearnersView?: boolean;
}) =>
  getLearnerTableColumnOptions({ programLevel, enableNewLearnersView })
    .filter((_) => _.default)
    .map((_) => _.accessor);

export const urlParameterDivider = '~';

export const INVITE_LEARN_MORE_URL = 'https://business.coursera.help/hc/articles/000002185';

export const LEARNER_RESULT_ATTRIBUTES = {
  name: ['RESULT_ATTRIBUTE_NAME', 'RESULT_ATTRIBUTE_EMAIL'],
  joinDate: ['RESULT_ATTRIBUTE_JOINED_AT'],
  invitationDate: ['RESULT_ATTRIBUTE_INVITED_AT'],
  programs: ['RESULT_ATTRIBUTE_PROGRAM_IDS'],
  contracts: ['RESULT_ATTRIBUTE_CONTRACT'],
  products: ['RESULT_ATTRIBUTE_PRODUCT_ITEM_IDS'],
  enrollments: ['RESULT_ATTRIBUTE_NUM_ENROLLMENTS'],
  completions: ['RESULT_ATTRIBUTE_NUM_COMPLETIONS'],
  externalId: ['RESULT_ATTRIBUTE_EXTERNAL_ID'],
  latestActivity: ['RESULT_ATTRIBUTE_LATEST_ACTIVITY_AT'],
  removalDate: ['RESULT_ATTRIBUTE_DELETED_AT'],
  allowReturn: ['RESULT_ATTRIBUTE_IS_SOFT_DELETED'],
  businessUnits: ['RESULT_ATTRIBUTE_BUSINESS_UNIT', 'RESULT_ATTRIBUTE_BUSINESS_UNIT_2'],
  jobTitle: ['RESULT_ATTRIBUTE_JOB_TITLE'],
  location: ['RESULT_ATTRIBUTE_LOCATION_COUNTRY', 'RESULT_ATTRIBUTE_LOCATION_REGION', 'RESULT_ATTRIBUTE_LOCATION_CITY'],
  managerEmail: ['RESULT_ATTRIBUTE_MANAGER_NAME'],
  managerName: ['RESULT_ATTRIBUTE_MANAGER_EMAIL'],
  jobType: ['RESULT_ATTRIBUTE_JOB_TYPE'],
} as const;
